import { BrandAsset } from '@/apis/core';

export interface BrandDetail {
  uid: string | null;
  referenceId: string | null;
  cspUid: string;
  status: string;
  displayName: string;
  entityType: string;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  ein: string | null;
  einIssuingCountry: string;
  altBusinessId: string | null;
  altBusinessIdType: string | null;
  phone: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  email: string;
  stockSymbol: string | null;
  stockExchange: string | null;
  brandRelationshipScore: number;
  website: string | null;
  submissionCount: number;
  vettedDate: Date | number | null;
  vertical: string;
  evpUid: string | null;
  evpVettingId: string | null;
  evpVettingScore: number | null;
  evpVettingClass: string | null;
  trustLevel: number;
  evpVettingDate: Date | number | null;
  createDate: Date | number;
  updateDate: Date | number;
  mobilePhone: string | null;
  mobilePhoneVerifiedDate: Date | number | null;
  universalEin: string | null;
  identityStatus: string;
  optionalAttributes?: OptionalAttributes;
  businessContactEmail?: string;
  businessContactEmailVerifiedDate?: number;
  businessContactFirstName?: string;
  businessContactLastName?: string;
  businessContactTitle?: string;
}

export interface OptionalAttributes {
  [key: string]: string;
}

export interface BrandFeedback {
  id: string;
  displayName: string;
  description: string;
  fields: string[];
  errors: {
    code: string;
    message: string;
  }[];
}

export interface VettingAppeal {
  evpUid: string | null;
  cspUid: string | null;
  brandUid: string | null;
  vettingId: string;
  vettingClass: string | null;
  status: string;
  categoryList: string;
  attachmentUuidList: string | null;
  explanation: string | null;
  appealOutcome: string | null;
  fullReport: string | null;
  statusUpdateDate: number;
  createDate: number;
  updateDate: number | null;
  attachments: AttachmentInfo[] | null;
}

export interface VettingAppealOutcomeFeedback {
  id: string;
  displayName: string;
  description: string;
  errors?: {
    code: string;
    message: string;
  }[];
}

export interface VettingAppealOutcome {
  vetStatus: string;
  vetScore?: number;
  optionalAttributes?: {
    governmentEntity?: boolean;
    section527?: boolean;
    taxExemptStatus?: string;
  };
  feedback?: {
    category?: VettingAppealOutcomeFeedback[];
    reasons?: string[];
  };
}

export interface Mno {
  networkId: number;
  displayName: string;
  osrBitmaskIndex: number;
}

export interface MnoSuspensionStatus {
  mnoId: number;
  suspended: boolean;
}

export interface MNOSuspendState {
  networkId: number;
  displayName: string;
  suspended?: boolean;
}

export interface AttachmentInfo {
  uuid: string;
  fileName: string;
  mimeType: string;
}

export interface AttachmentInfoExtras extends AttachmentInfo {
  file: File;
}

export interface AppealCategoryData {
  appealCategoryId: string;
  displayName: string;
  description: string;
  evpSupport: string[];
  vettingType: string[];
}

export interface AppealRequestData {
  categories: string[];
  explanation: string;
  attachments: string[];
}

export interface ExtVettingRecord {
  evpUid: string;
  brandUid: string;
  vettingClass: string;
  vettingId: string;
  vettingToken: string | null;
  tcrBillable: boolean;
  tcrBillAmount: number | null;
  vettedDate: number | Date | null;
  score: number | null;
  fullReport: string | null;
  vettingStatus: string;
  reasons: string | null;
  additionalRequestPayload: string | null;
  createDate: number | Date;
  updateDate: number | Date;
  expirationDate: number | Date | null;
  validUntil: number | Date | null;
  authenticationPlusStatus: string;
}

export enum EditModal {
  company = 'company',
  contact = 'contact',
  companyName = 'companyName',
  brandRelationship = 'brandRelationship',
}

export enum ModalType {
  newVetting = 1,
  vettingResult = 2,
  importVetting = 3,
  vettingStatus = 4,
  politicalVetting = 5,
}

export type VettingClassType =
  | 'STANDARD'
  | 'ENHANCED'
  | 'POLITICAL'
  | 'EXEMPT'
  | 'POLITICAL_FEDERAL'
  | 'POLITICAL_STATE'
  | 'POLITICAL_LOCAL'
  | 'RCS'
  | 'AUTHPLUS';
export interface ImportVettingInstruction {
  info: string;
  parameters: {
    vettingId: { label: string; show: boolean };
    vettingToken: { label: string; show: boolean };
  };
  supported: boolean;
}
export interface NewVettingInstruction {
  info: string;
  price: {
    [key in VettingClassType]?: number;
  };
  supported: boolean;
  waitTime: string;
}
export interface VettingProvider {
  displayName: string;
  tcrBillable: boolean;
  uid: string;
  vettingClasses: VettingClassType[];
  vettingInstruction: {
    importVet: ImportVettingInstruction;
    newVet: NewVettingInstruction;
  };
}

export interface VerticalType {
  id: string;
  displayName: string;
  description: string;
}

export { BrandAssetStatus, BrandAssetType } from '@/apis/core';
export type { BrandAsset } from '@/apis/core';
export type { BrandAssetResult as RegisteredBrandAsset } from '@/apis/core';

export interface BrandAssetExtended extends BrandAsset {
  file?: File;
}

export interface BrandAssetAppeal {
  brandId: string;
  assetName: string;
  appealStatus: string;
  categoryList: string[];
  attachmentUuidList: string[];
  explanation: string;
  appealOutcome: {
    vetStatus: string;
    feedback: {
      reasons: string[];
    };
  };
  appealStatusUpdateDate: number | Date | null;
  createDate: number | Date;
}

export enum BrandStatus {
  Active = 'ACTIVE',
  Deleted = 'DELETED',
}

export interface ValidationRule {
  entityType: string;
  attributeName: string;
  country: string;
  regulation: string;
  structure: string;
  example: string;
  regex: string;
  referenceLink: string;
}

export interface BrandDetailsFormValues {
  companyName: string;
  displayName: string;
  entityType: string;
  country: string;
  ein: string;
  einIssuingCountry: string;
  altBusinessIdType: string;
  altBusinessId: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  website: string;
  stockSymbol: string;
  stockExchange: string;
  vertical: string;
  referenceId: string;
  firstName: string;
  lastName: string;
  mobilePhone: string;
}

export interface BrandRelationshipFormValues {
  brandRelationshipScore: number;
}

export interface ContactDetailsFormValues {
  email: string;
  phone: string;
  businessContactEmail: string;
}

export interface AuthenticationPlusStatus {
  id: string;
  displayName: string;
}
